import React, { type PropsWithChildren } from 'react';
import { StyleSheet, View, type ViewProps } from 'react-native';

import { theme, useResponsive } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * One row of a category in a virtualized menu, where a category is split
 * across independently mounted rows and `MenuCategoriesGrid.Category` cannot
 * wrap it as a whole. Owns the category spacing: the first row carries the top
 * spacing, the last row the bottom spacing, and a single-row category both.
 */
export const MenuCategoriesGridCategoryRow = (props: Props) => {
  const { position, children, style, ...restProps } = props;

  const { match } = useResponsive();

  // ─── Styles ──────────────────────────────────────────────────────────

  const shouldSpaceTop = position === 'first' || position === 'only';
  const shouldSpaceBottom = position === 'last' || position === 'only';

  const topSpacingStyles = match([styles.topSpacingXS, styles.topSpacingSM]);
  const bottomSpacingStyles = match([styles.bottomSpacingXS, undefined]);
  const containerStyles = [
    shouldSpaceTop ? topSpacingStyles : undefined,
    shouldSpaceBottom ? bottomSpacingStyles : undefined,
    style,
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={containerStyles} {...restProps}>
      {children}
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  topSpacingXS: {
    paddingTop: theme.spacing['6'],
  },
  topSpacingSM: {
    paddingTop: theme.spacing['14'],
  },
  bottomSpacingXS: {
    paddingBottom: theme.spacing['10'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<
  ViewProps & {
    position: 'first' | 'middle' | 'last' | 'only';
  }
>;
